import type { ComponentType, JSX } from 'react';
import type { AppDeclaree, FicheApp } from './app-declaree.js';
import { EcranAppCanvas } from './EcranAppCanvas.js';
import { EcranDeclare } from './EcranDeclare.js';
import { ReglagesAppCanvas } from './ReglagesAppCanvas.js';
import { ReglagesDeclares } from './ReglagesDeclares.js';
import { actionsDuDock, declarationDEntree } from './entree.js';
import type { ActionDuDock, DeclarationDEntree } from './entree.js';

/**
 * L'ENTREE DE REGISTRE D'UNE APP DECLAREE : ce que l'hote pose dans son
 * `ShellAppRegistry`, sans qu'aucun ecran ne s'ecrive.
 *
 * ELLE EST STRUCTURELLEMENT COMPATIBLE AVEC `ShellAppDeclaration`, et ce package
 * n'importe PAS `@taki/shell` : l'ordre de build est ui -> app-runtime -> shell,
 * et l'inverser ferait dependre le chrome du runtime des apps. C'est du typage
 * structurel, le meme procede que `@taki/ui` emploie pour consommer un
 * `taki.screens.json` sans connaitre Zod : l'hote ne compile que si les deux
 * formes concordent, et le jour ou le contrat du shell gagne une clef, ce
 * fichier cesse de compiler chez lui.
 *
 * LES DEUX LOADERS SONT `async` SANS `import()`, et ce n'est pas un oubli : le
 * composant est le MEME pour toutes les apps declarees (il vit dans ce package,
 * deja dans le bundle), il n'y a aucun morceau a aller chercher. Ce qui varie
 * d'une app a l'autre, ce sont ses trois fichiers de donnees, et ils sont deja
 * la aussi.
 *
 * UNE APP CANVAS PASSE PAR LA MEME FABRIQUE, et son morceau a la demande est
 * AILLEURS : l'ecran (`EcranAppCanvas`) est lui aussi dans ce package, c'est sa
 * SURFACE qui arrive par `appCanvas.charger`, un vrai `import()` que l'ecran
 * resout sous `Suspense`. L'entree reste donc `entreeDeRegistre(<id>App)`, une
 * ligne, et le registre de l'hote ne connait pas la difference.
 */

/** Ce qu'une app declaree expose au registre de l'hote. */
export interface EntreeDeRegistre {
  id: string;
  fiche: FicheApp;
  load: () => Promise<{ default: ComponentType }>;
  loadSettings: () => Promise<{ default: ComponentType }>;
  actions?: ActionDuDock[];
  /**
   * LA DECLARATION BRUTE, portee par l'entree, et lue par le Studio client
   * (`packages/shell/src/studio/client/`) pour montrer a une org la declaration
   * de SES apps et la reecrire pour elle. ABSENTE pour une App Canvas : elle n'a
   * ni champ ni porte a personnaliser (`declarationDEntree`, `./entree.ts`).
   */
  declaration?: DeclarationDEntree;
}

export function entreeDeRegistre(app: AppDeclaree): EntreeDeRegistre {
  const actions = actionsDuDock(app);

  /*
   * LES DEUX COMPOSANTS SONT CREES UNE FOIS, ET C'EST NECESSAIRE : un composant
   * fabrique a chaque appel de `load()` change d'IDENTITE, et React remonte
   * alors l'arbre entier au lieu de le mettre a jour. Un ecran qui se remonte
   * perd sa fiche ouverte, sa recherche et sa position de defilement, sans
   * qu'aucune erreur ne le dise.
   *
   * LE REGIME CHOISIT LA PAIRE : une App Canvas monte son gabarit reduit et
   * ses reglages sans porte, une app a portes l'ecran generique.
   */
  const appCanvas = app.appCanvas !== null;
  const Ecran = (): JSX.Element =>
    appCanvas ? <EcranAppCanvas app={app} /> : <EcranDeclare app={app} />;
  Ecran.displayName = `${appCanvas ? 'EcranAppCanvas' : 'EcranDeclare'}(${app.id})`;
  const Reglages = (): JSX.Element =>
    appCanvas ? <ReglagesAppCanvas app={app} /> : <ReglagesDeclares app={app} />;
  Reglages.displayName = `${appCanvas ? 'ReglagesAppCanvas' : 'ReglagesDeclares'}(${app.id})`;

  const declaration = declarationDEntree(app);

  return {
    id: app.id,
    fiche: app.fiche,
    load: async () => ({ default: Ecran }),
    loadSettings: async () => ({ default: Reglages }),
    ...(actions.length === 0 ? {} : { actions }),
    ...(declaration === undefined ? {} : { declaration }),
  };
}
